import { getWidgetMergeMap, walkCollections, widgetItemIdentity, } from "./merge-map.js";
import { copyBackPersonLinks } from "./person-fields.js";
import { getWidgetParamsSchema } from "./registry.js";
/**
 * Canonical, stable string form of a path — the key used for revisions,
 * tombstones, edit sessions and audit. Chosen so the three bracket styles are
 * unambiguous and no separator can appear in a segment name:
 * `branches[br-1]/causes[c-2]/text`, `rows[r-1]/cells{col-3}`, `por/evidence`.
 */
export function widgetTargetKey(path) {
    return path
        .map((segment) => {
        switch (segment.kind) {
            case "prop":
                return segment.name;
            case "collection":
                return segment.name;
            case "item":
                return `${segment.collection}[${segment.itemId}]`;
            case "entry":
                return `${segment.record}{${segment.key}}`;
        }
    })
        .join("/");
}
/** True when `key` is `ancestor` or lies beneath it. */
export function isDescendantTargetKey(key, ancestor) {
    return key === ancestor || key.startsWith(`${ancestor}/`);
}
/** Keeps a widget row's metadata bounded; oldest tombstones are dropped. */
export const WIDGET_TOMBSTONE_LIMIT = 500;
export function emptyWidgetContentMeta(version) {
    return {
        v: 1,
        epoch: 0,
        syncedVersion: version,
        targets: {},
        tombstones: {},
    };
}
/**
 * Reconcile stored metadata with the row it belongs to.
 *
 * A `syncedVersion` behind the live row version means someone wrote `params`
 * without maintaining this metadata (a legacy PATCH, a module tool, the VSM raw
 * `jsonb_set`). The recorded revisions may describe values that no longer
 * exist, so the epoch advances and every previously-issued AI baseline stops
 * matching. Human edits are unaffected.
 */
export function reconcileWidgetContentMeta(meta, version, acceptedContent) {
    if (!meta || meta.v !== 1)
        return emptyWidgetContentMeta(version);
    if (meta.syncedVersion === version)
        return meta;
    // Only an accepted whole-content write can restore an identity. A delayed
    // command still encounters the retained marker for every genuinely absent item.
    const map = acceptedContent?.kind === "sipoc" ? getWidgetMergeMap("sipoc") : null;
    const tombstones = map && acceptedContent
        ? Object.fromEntries(Object.entries(meta.tombstones).filter(([key]) => {
            const path = parseTargetKey(key);
            return (path === null ||
                resolveContainer(acceptedContent.params, path, map) === undefined);
        }))
        : meta.tombstones;
    return { ...meta, epoch: meta.epoch + 1, syncedVersion: version, tombstones };
}
/**
 * Whether ONE named target moved since a given read of the widget.
 *
 * The same question `checkBaseline` asks per operation, exposed for a module
 * that merges by its OWN domain operations rather than through the executor —
 * Hoshin's workshop ideas, whose three leaves (`text`, `color`, `parked`) are
 * written by its own service under its own participant, step and authorship
 * gates (ADR-0063). Reusing the executor there would mean registering a widget
 * kind that no shared catalogue publishes; reusing THIS keeps one definition of
 * "did this field move", which is the part that must not be answered twice.
 *
 * `readVersion` is the row `version` the caller last read. A target with no
 * recorded revision, or one recorded at or before that version, did not
 * demonstrably move — which is deliberately the permissive reading, matching
 * `changedAt`'s docblock: a metadata row written before PB-4923 proves nothing,
 * and inventing a conflict from an absence would refuse edits that are fine.
 */
export function widgetTargetChangedSince(meta, targetKey, readVersion) {
    const target = meta.targets[targetKey];
    if (!target || (target.changedAt ?? 0) <= readVersion)
        return { changed: false };
    return {
        changed: true,
        ...(target.actorId === null ? {} : { actorId: target.actorId }),
    };
}
/**
 * Stamp revisions for the targets a module-owned write just changed.
 *
 * The counterpart to `widgetTargetChangedSince`, and the same bump the executor
 * performs internally — `revision` counts up so an unchanged target is
 * distinguishable from one written back to its old value, and `changedAt` is
 * the row version the write produces, which is what makes the check above
 * answerable as of an earlier read.
 *
 * `syncedVersion` is set to `version` because the caller IS maintaining this
 * metadata; leaving it behind would make the next `reconcileWidgetContentMeta`
 * treat the module's own write as unmanaged drift and burn every AI baseline.
 */
export function recordWidgetTargetRevisions(meta, targetKeys, attribution) {
    if (targetKeys.length === 0)
        return meta;
    const targets = { ...meta.targets };
    for (const key of targetKeys) {
        targets[key] = {
            revision: (targets[key]?.revision ?? 0) + 1,
            actorId: attribution.actorId,
            source: attribution.source,
            changedAt: attribution.version,
        };
    }
    return { ...meta, syncedVersion: attribution.version, targets };
}
export class WidgetOperationRejection extends Error {
    detail;
    constructor(message, detail) {
        super(message);
        this.detail = detail;
        this.name = "WidgetOperationRejection";
    }
}
// --------------------------------------------------------------- the executor
export function applyWidgetOperations(input) {
    const map = getWidgetMergeMap(input.kind);
    if (map.disposition !== "operations") {
        throw new WidgetOperationRejection(`Widget kind '${input.kind}' is not operation-merged (${map.disposition}).`, {});
    }
    if (input.operations.length === 0) {
        throw new WidgetOperationRejection("A command needs at least one operation.", {});
    }
    const schema = getWidgetParamsSchema(input.kind, input.schemaVersion);
    const editsByTarget = new Map((input.activeEdits ?? []).map((edit) => [edit.targetKey, edit]));
    let working = clone(input.params);
    let targets = { ...input.meta.targets };
    let tombstones = {
        ...input.meta.tombstones,
    };
    const applied = [];
    const skipped = [];
    const changed = new Set();
    for (const group of groupOperations(input.operations)) {
        const candidate = clone(working);
        const candidateTargets = { ...targets };
        const candidateTombstones = { ...tombstones };
        const groupChanged = [];
        const groupApplied = [];
        let failure = null;
        for (const operation of group) {
            const outcome = applyOne(operation, {
                map,
                candidate,
                baselineParams: input.params,
                targets: candidateTargets,
                tombstones: candidateTombstones,
                meta: input.meta,
                editsByTarget,
                source: input.source,
                actorId: input.actorId,
                now: input.now,
            });
            if (outcome.skipped) {
                failure = outcome.skipped;
                break;
            }
            groupApplied.push(outcome.applied);
            groupChanged.push(...outcome.changedTargets);
        }
        if (failure === null) {
            // Domain invariants are only expressible over the whole object — capacity,
            // the three-level funnel, generic-table cell/column coherence, the
            // process-trace validated/evidence pairing. Validating the candidate here
            // is what makes an atomic group's "all or nothing" real: an invalid group
            // is discarded whole rather than leaving half a move behind.
            //
            // Person links first (assign-person d37): an operation that writes a
            // person's text alone — every V17 AI input — keeps the stored link while
            // the text is unchanged and drops it when the text changes. A Generic
            // Table user cell would otherwise fail the schema for want of `userId`
            // before any server copy-back could run. An AI command may never ADD a
            // link: V17 AI inputs carry names only, and an id is the person picker's
            // to set (ADR-0095), so a new id on the AI path refuses the group. A
            // human command writes exact values — an explicit `null` is an unlink —
            // so only the links it leaves ABSENT are filled.
            const linked = copyBackPersonLinks(input.kind, candidate, working, {
                absentOnly: input.source !== "ai",
            });
            const validation = schema.safeParse(linked.params);
            if (input.source === "ai" && linked.newPersonIds.length > 0) {
                failure = {
                    operationId: group[0].operationId,
                    reason: "invalid",
                    targetKey: widgetTargetKey(group[0].target),
                    issues: [
                        "AI writes carry person names only; a person link is set by the people picker.",
                    ],
                };
            }
            else if (!validation.success) {
                failure = {
                    operationId: group[0].operationId,
                    reason: "invalid",
                    targetKey: widgetTargetKey(group[0].target),
                    issues: validation.error.issues.map((issue) => `${issue.path.join(".")}: ${issue.message}`),
                };
            }
            else {
                working = validation.data;
                targets = candidateTargets;
                tombstones = candidateTombstones;
                applied.push(...groupApplied);
                for (const key of groupChanged)
                    changed.add(key);
                continue;
            }
        }
        // Group atomicity: the operation that could not apply keeps its own reason;
        // every sibling in the group is reported as skipped for the dependency, so
        // the receipt says why each one did not land rather than silently dropping.
        for (const operation of group) {
            skipped.push(operation.operationId === failure.operationId
                ? failure
                : {
                    operationId: operation.operationId,
                    reason: "dependency_skipped",
                    targetKey: widgetTargetKey(operation.target),
                });
        }
    }
    const prunedTargets = pruneTargets(targets, working, map);
    const meta = {
        v: 1,
        epoch: input.meta.epoch,
        syncedVersion: input.meta.syncedVersion,
        targets: prunedTargets,
        tombstones: capTombstones(tombstones),
    };
    return {
        params: working,
        meta,
        applied,
        skipped,
        changedTargets: [...changed].sort(),
        /*
         * Derived from what CHANGED, not from what applied.
         *
         * Every operation that mutates reports at least one changed target, so this
         * is the same answer as `applied.length === 0` for all of them. It differs
         * for the one operation that can succeed without changing anything:
         * parking an already-parked idea is a successful no-op, and reading it as
         * "changed" would consume a widget version, rewrite the row and nudge every
         * peer to re-read content that is identical.
         */
        unchanged: changed.size === 0,
    };
}
/**
 * Build the baseline for a set of operations from an authoritative read.
 *
 * This exists so no caller has to assemble a baseline by hand, and above all so
 * no MODEL ever supplies one: the plan is explicit that a baseline is attached
 * by trusted server code from the read that materialized the proposal. A model
 * that could name its own revisions could name the current ones and defeat the
 * whole check.
 *
 * What it records, and nothing else:
 *
 *  - the revision of each operation's own target (and a move's destination),
 *    so an unrelated field moving does not block an unrelated operation;
 *  - for a removal, the ids of every collection BENEATH the item being removed,
 *    so a delete cannot silently take a child somebody added since the read.
 *    Not the item's own collection — a sibling appearing next to it is no
 *    reason to refuse.
 */
export function captureWidgetOperationBaseline(input) {
    const map = getWidgetMergeMap(input.kind);
    const revisions = {};
    const members = {};
    for (const operation of input.operations) {
        const targetKey = widgetTargetKey(operation.target);
        revisions[targetKey] = input.meta.targets[targetKey]?.revision ?? 0;
        if (operation.to !== undefined) {
            const toKey = widgetTargetKey(operation.to);
            revisions[toKey] = input.meta.targets[toKey]?.revision ?? 0;
        }
        if (operation.kind !== "remove_item")
            continue;
        for (const key of descendantCollectionKeys(map, operation.target)) {
            const ids = collectionItemIdsAt(input.params, map, key);
            if (ids !== null)
                members[key] = ids;
        }
    }
    return {
        epoch: input.meta.epoch,
        revisions,
        ...(Object.keys(members).length > 0 ? { members } : {}),
        ...(input.managed ? {} : { unmanagedVersion: input.meta.syncedVersion }),
        ...(input.readVersion === undefined
            ? {}
            : { readVersion: input.readVersion }),
    };
}
/** Canonical keys of every collection nested beneath an item target. */
function descendantCollectionKeys(map, itemPath) {
    const spec = resolveCollectionSpec(map, itemPath);
    if (spec === null)
        return [];
    const prefix = widgetTargetKey(itemPath);
    // One level only: a grandchild lives under an ITEM of the child, and no
    // single key can name it without that item's id. Nothing in the register
    // nests three deep, and inventing per-item keys here would record membership
    // the executor never asks about.
    return (spec.children ?? []).map((child) => `${prefix}/${child.collection}`);
}
function applyOne(operation, ctx) {
    const targetKey = widgetTargetKey(operation.target);
    const skip = (reason, extra) => ({
        skipped: {
            operationId: operation.operationId,
            reason,
            targetKey,
            ...extra,
        },
    });
    const spec = resolveSpec(ctx.map, operation);
    if (spec === null)
        return skip("unsupported_target");
    // A resurrection guard that runs before anything else: once an item id has a
    // tombstone, no operation may bring it back or pretend it is still there,
    // however delayed its retry. Checked SHALLOWEST-first so a stale edit to a
    // cause inside a deleted branch is reported against the branch — the thing
    // that actually went away — rather than against the leaf.
    const tombstoned = tombstonedAncestor(operation, ctx);
    if (tombstoned !== null) {
        return skip("item_deleted", {
            targetKey: tombstoned,
            ...attribution(ctx.tombstones[tombstoned].actorId),
        });
    }
    const baselineSkip = checkBaseline(operation, ctx, targetKey);
    if (baselineSkip !== null)
        return { skipped: baselineSkip };
    const editSkip = checkActiveEdit(operation, ctx, targetKey);
    if (editSkip !== null)
        return { skipped: editSkip };
    switch (operation.kind) {
        case "set_field":
            return applySetField(operation, ctx, targetKey, spec);
        case "add_item":
            return applyAddItem(operation, ctx, targetKey, spec);
        case "remove_item":
            return applyRemoveItem(operation, ctx, targetKey);
        case "move_item":
            return applyMoveItem(operation, ctx, targetKey, spec);
        case "replace_item":
            return applyReplaceItem(operation, ctx, targetKey);
        case "exclude_item":
        case "include_item":
            return applyExclusion(operation, ctx, targetKey);
    }
}
/**
 * Park or restore ONE item, by adding or removing its id from the kind's
 * declared exclusion set.
 *
 * Every protection the other operations get is already applied by the
 * dispatcher above: `resolveSpec` has confirmed the kind declares an exclusion
 * and that this is the set it declared, the tombstone guard has run, the AI
 * baseline is fenced against this member's own revision, and a live edit
 * session on it defers the operation. What is left here is the membership
 * itself, and the two rules that make it safe to retry:
 *
 *  - the id must still name a LIVE item of the exclusion's collection, so a
 *    restore cannot resurrect a deleted idea and a park cannot invent one;
 *  - the operation is IDEMPOTENT. Parking an already-parked idea, or restoring
 *    one that is not parked, is a successful no-op — it applies, bumps nothing
 *    and reports no changed target, so a retry can never produce a duplicate id
 *    and an all-no-op command still consumes no version.
 */
function applyExclusion(operation, ctx, targetKey) {
    const exclusion = ctx.map.exclusion;
    const leaf = operation.target[operation.target.length - 1];
    if (exclusion === undefined || leaf.kind !== "entry") {
        return skipOf(operation, "unsupported_target", targetKey);
    }
    const itemId = leaf.key;
    const params = asRecord(ctx.candidate);
    if (params === undefined) {
        return skipOf(operation, "item_deleted", targetKey);
    }
    const spec = (ctx.map.collections ?? []).find((candidate) => candidate.collection === exclusion.collection);
    const items = params[exclusion.collection];
    if (spec === undefined || !Array.isArray(items)) {
        return skipOf(operation, "unsupported_target", targetKey);
    }
    if (!items.some((item) => widgetItemIdentity(spec, item) === itemId)) {
        return skipOf(operation, "item_deleted", targetKey);
    }
    const current = params[exclusion.valueSet];
    const members = Array.isArray(current)
        ? current.filter((value) => typeof value === "string")
        : [];
    const parked = members.includes(itemId);
    const wanted = operation.kind === "exclude_item";
    const applied = {
        operationId: operation.operationId,
        kind: operation.kind,
        targetKey,
    };
    if (parked === wanted) {
        // The successful no-op. Reported as applied so the receipt says the
        // outcome was reached, with no changed target so nothing is written.
        return { skipped: null, applied, changedTargets: [] };
    }
    params[exclusion.valueSet] = wanted
        ? [...members, itemId]
        : members.filter((value) => value !== itemId);
    bump(ctx, targetKey);
    return { skipped: null, applied, changedTargets: [targetKey] };
}
/**
 * Replace one opaque item's whole body, in place.
 *
 * Every protection the other item operations get is already applied by the
 * dispatcher above — the tombstone guard (so a deleted element cannot be
 * resurrected by a replace), the AI baseline fence, and the active-edit lease.
 * This function only has to be honest about identity and order.
 */
function applyReplaceItem(operation, ctx, targetKey) {
    const leaf = operation.target[operation.target.length - 1];
    if (leaf.kind !== "item") {
        return skipOf(operation, "unsupported_target", targetKey);
    }
    const collectionPath = [
        ...operation.target.slice(0, -1),
        { kind: "collection", name: leaf.collection },
    ];
    const spec = resolveCollectionSpec(ctx.map, collectionPath);
    // The opt-in is the gate. A collection with addressable leaves must keep
    // using `set_field`, or one careless `replace_item` would throw away a
    // concurrent edit to a sibling field the caller never meant to touch.
    if (spec === null ||
        spec.itemIdField === null ||
        spec.opaqueItemBody !== true) {
        return skipOf(operation, "unsupported_target", targetKey);
    }
    const array = resolveArray(ctx.candidate, collectionPath, ctx.map);
    if (array === null)
        return skipOf(operation, "item_deleted", targetKey);
    const body = asRecord(operation.value);
    if (body === undefined) {
        return skipOf(operation, "invalid", targetKey, {
            issues: [`${spec.collection}: replacement body must be an object`],
        });
    }
    // The id is the ADDRESS. A body carrying a different one is a move or a
    // rename wearing a replace's clothes, and applying it would silently change
    // which item every stored target key refers to.
    if (body[spec.itemIdField] !== leaf.itemId) {
        return skipOf(operation, "invalid", targetKey, {
            issues: [`${spec.collection}: replacement id must match the target`],
        });
    }
    const matches = array.filter((entry) => widgetItemIdentity(spec, entry) === leaf.itemId);
    if (matches.length === 0)
        return skipOf(operation, "item_deleted", targetKey);
    if (matches.length > 1) {
        return skipOf(operation, "invalid", targetKey, {
            issues: [`${spec.collection}: item id is ambiguous`],
        });
    }
    const index = array.findIndex((entry) => widgetItemIdentity(spec, entry) === leaf.itemId);
    // In place, so a replace never reorders the board.
    array[index] = clone(body);
    bump(ctx, targetKey);
    return {
        skipped: null,
        applied: {
            operationId: operation.operationId,
            kind: "replace_item",
            targetKey,
        },
        // The item only. Membership did not change, so bumping the COLLECTION
        // would make an unrelated add or remove read as stale.
        changedTargets: [targetKey],
    };
}
// --------------------------------------------------------------- skip checks
function checkBaseline(operation, ctx, targetKey) {
    const baseline = operation.baseline;
    if (baseline === undefined)
        return null;
    // A baseline captured before an unaccounted write cannot be trusted at all,
    // so the whole operation is preserved rather than applied. This is the
    // fail-closed arm the legacy-writer boundary makes necessary.
    if (baseline.epoch !== ctx.meta.epoch) {
        return {
            operationId: operation.operationId,
            reason: "field_changed",
            targetKey,
        };
    }
    // Captured while the widget had no metadata at all: nothing recorded what
    // moved, so the row version is the only evidence there is. See
    // `WidgetOperationBaseline.unmanagedVersion`.
    if (baseline.unmanagedVersion !== undefined &&
        baseline.unmanagedVersion !== ctx.meta.syncedVersion) {
        return {
            operationId: operation.operationId,
            reason: "field_changed",
            targetKey,
        };
    }
    for (const [key, expected] of Object.entries(baseline.revisions)) {
        // Compare with the state before this batch, not revisions from our own edits.
        const target = ctx.meta.targets[key];
        const current = target?.revision ?? 0;
        if (current !== expected) {
            return {
                operationId: operation.operationId,
                reason: "field_changed",
                targetKey: key,
                ...attribution(target?.actorId),
            };
        }
        /* The revisions agree — but they were captured when the PROPOSAL was
           prepared, and the model read this widget earlier than that. If the target
           changed in between, the capture recorded the post-edit revision as if it
           were what the model saw, and the comparison above cannot notice.
    
           `changedAt` closes it: a target that moved after the read is refused even
           though its revision matches, which is the whole point of AC4. */
        if (baseline.readVersion !== undefined &&
            (target?.changedAt ?? 0) > baseline.readVersion) {
            return {
                operationId: operation.operationId,
                reason: "field_changed",
                targetKey: key,
                ...attribution(target?.actorId),
            };
        }
    }
    // Membership: a remove whose subtree gained an item since the proposal was
    // prepared must not take that item with it. Deleting a fishbone category
    // cannot silently remove a cause a human added underneath it.
    for (const [collectionKey, expectedIds] of Object.entries(baseline.members ?? {})) {
        const actual = collectionItemIdsAt(ctx.baselineParams, ctx.map, collectionKey);
        if (actual === null)
            continue;
        if (!sameIdSet(actual, expectedIds)) {
            return {
                operationId: operation.operationId,
                reason: "field_changed",
                targetKey: collectionKey,
            };
        }
    }
    return null;
}
function checkActiveEdit(operation, ctx, targetKey) {
    // Only AI defers to a live human edit. Two humans on one field is D2's
    // last-accepted-server-save-wins, and blocking there would be the "widget-wide
    // locking" alternative the decision log rejects.
    if (ctx.source !== "ai")
        return null;
    for (const [key, edit] of ctx.editsByTarget) {
        // An edit anywhere beneath a removal also protects it, so an AI delete
        // cannot land on a subtree somebody is typing into.
        const collides = operation.kind === "remove_item"
            ? isDescendantTargetKey(key, targetKey)
            : key === targetKey;
        if (collides) {
            return {
                operationId: operation.operationId,
                reason: "editing_in_progress",
                targetKey: key,
                actorId: edit.userId,
            };
        }
    }
    return null;
}
// --------------------------------------------------------------- mutations
function applySetField(operation, ctx, targetKey, spec) {
    const leaf = operation.target[operation.target.length - 1];
    if (leaf.kind !== "prop" && leaf.kind !== "entry") {
        return {
            skipped: {
                operationId: operation.operationId,
                reason: "unsupported_target",
                targetKey,
            },
        };
    }
    const parent = resolveContainer(ctx.candidate, operation.target.slice(0, -1), ctx.map);
    if (parent === undefined) {
        return {
            skipped: {
                operationId: operation.operationId,
                reason: "item_deleted",
                targetKey,
            },
        };
    }
    void spec;
    const holder = leaf.kind === "prop" ? parent : asRecord(parent)?.[leaf.record];
    if (!isRecord(holder)) {
        return {
            skipped: {
                operationId: operation.operationId,
                reason: "item_deleted",
                targetKey,
            },
        };
    }
    const key = leaf.kind === "prop" ? leaf.name : leaf.key;
    if (operation.value === undefined)
        delete holder[key];
    else
        holder[key] = clone(operation.value);
    bump(ctx, targetKey);
    return {
        skipped: null,
        applied: {
            operationId: operation.operationId,
            kind: "set_field",
            targetKey,
        },
        changedTargets: [targetKey],
    };
}
function applyAddItem(operation, ctx, targetKey, spec) {
    const leaf = operation.target[operation.target.length - 1];
    if (leaf.kind !== "collection" || spec.collection === null) {
        return skipOf(operation, "unsupported_target", targetKey);
    }
    const collection = spec.collection;
    if (collection.itemIdField === null) {
        return skipOf(operation, "unsupported_target", targetKey);
    }
    if (collection.fixedLength !== undefined) {
        return skipOf(operation, "unsupported_target", targetKey);
    }
    const array = resolveArray(ctx.candidate, operation.target, ctx.map);
    if (array === null)
        return skipOf(operation, "item_deleted", targetKey);
    const item = operation.value;
    if (!isRecord(item))
        return skipOf(operation, "unsupported_target", targetKey);
    const itemId = widgetItemIdentity(collection, item);
    if (itemId === null) {
        return skipOf(operation, "unsupported_target", targetKey);
    }
    const itemKey = `${targetKey}[${itemId}]`;
    if (ctx.tombstones[itemKey] !== undefined) {
        return skipOf(operation, "item_deleted", itemKey);
    }
    // Uniqueness is enforced HERE and not in the Zod schema: only four of the
    // id-bearing collections carry a uniqueness refinement today, and persisted
    // rows may already hold duplicates from five different client-side id
    // generators. Tightening the schema would fail validation on existing
    // content; refusing the ambiguous write does not. The same reasoning is what
    // lets a composite key (a matrix cell's severity+level) be an identity at all.
    if (array.some((existing) => widgetItemIdentity(collection, existing) === itemId)) {
        return skipOf(operation, "invalid", itemKey, {
            issues: [`${collection.collection}: duplicate item id`],
        });
    }
    if (collection.maxItems !== undefined &&
        array.length >= collection.maxItems) {
        return skipOf(operation, "capacity_exceeded", targetKey);
    }
    array.push(clone(item));
    bump(ctx, targetKey);
    bump(ctx, itemKey);
    return {
        skipped: null,
        applied: {
            operationId: operation.operationId,
            kind: "add_item",
            targetKey: itemKey,
        },
        changedTargets: [targetKey, itemKey],
    };
}
function applyRemoveItem(operation, ctx, targetKey) {
    const leaf = operation.target[operation.target.length - 1];
    if (leaf.kind !== "item") {
        return skipOf(operation, "unsupported_target", targetKey);
    }
    const collectionPath = [
        ...operation.target.slice(0, -1),
        { kind: "collection", name: leaf.collection },
    ];
    const array = resolveArray(ctx.candidate, collectionPath, ctx.map);
    if (array === null)
        return skipOf(operation, "item_deleted", targetKey);
    const spec = resolveCollectionSpec(ctx.map, collectionPath);
    if (spec === null || spec.itemIdField === null) {
        return skipOf(operation, "unsupported_target", targetKey);
    }
    if (spec.fixedLength !== undefined) {
        return skipOf(operation, "unsupported_target", targetKey);
    }
    const matches = array.filter((entry) => widgetItemIdentity(spec, entry) === leaf.itemId);
    if (matches.length === 0)
        return skipOf(operation, "item_deleted", targetKey);
    // A duplicate id makes `.find()` a coin flip. Refuse rather than guess.
    if (matches.length > 1) {
        return skipOf(operation, "invalid", targetKey, {
            issues: [`${spec.collection}: item id is ambiguous`],
        });
    }
    const index = array.findIndex((entry) => widgetItemIdentity(spec, entry) === leaf.itemId);
    array.splice(index, 1);
    const collectionKey = widgetTargetKey(collectionPath);
    // Drop the removed subtree's revisions and tombstone it, so a delayed add
    // retry for the same id can never resurrect it (AC7 / AC8).
    for (const key of Object.keys(ctx.targets)) {
        if (isDescendantTargetKey(key, targetKey))
            delete ctx.targets[key];
    }
    ctx.tombstones[targetKey] = {
        at: ctx.now,
        actorId: ctx.actorId,
        source: ctx.source,
    };
    bump(ctx, collectionKey);
    return {
        skipped: null,
        applied: {
            operationId: operation.operationId,
            kind: "remove_item",
            targetKey,
        },
        changedTargets: [collectionKey, targetKey],
    };
}
function applyMoveItem(operation, ctx, targetKey, spec) {
    const leaf = operation.target[operation.target.length - 1];
    if (leaf.kind !== "item" || operation.to === undefined) {
        return skipOf(operation, "unsupported_target", targetKey);
    }
    const toLeaf = operation.to[operation.to.length - 1];
    if (toLeaf?.kind !== "collection") {
        return skipOf(operation, "unsupported_target", targetKey);
    }
    const fromPath = [
        ...operation.target.slice(0, -1),
        { kind: "collection", name: leaf.collection },
    ];
    const fromSpec = resolveCollectionSpec(ctx.map, fromPath);
    const toSpec = resolveCollectionSpec(ctx.map, operation.to);
    if (fromSpec === null || toSpec === null) {
        return skipOf(operation, "unsupported_target", targetKey);
    }
    if (fromSpec.itemIdField === null) {
        return skipOf(operation, "unsupported_target", targetKey);
    }
    // Moving between two different collections would change which item schema the
    // value must satisfy; only same-shape moves are representable.
    if (fromSpec.itemIdField !== toSpec.itemIdField) {
        return skipOf(operation, "unsupported_target", targetKey);
    }
    const from = resolveArray(ctx.candidate, fromPath, ctx.map);
    const to = resolveArray(ctx.candidate, operation.to, ctx.map);
    if (from === null || to === null) {
        return skipOf(operation, "item_deleted", targetKey);
    }
    const index = from.findIndex((entry) => widgetItemIdentity(fromSpec, entry) === leaf.itemId);
    if (index === -1)
        return skipOf(operation, "item_deleted", targetKey);
    if (from !== to &&
        toSpec.maxItems !== undefined &&
        to.length >= toSpec.maxItems) {
        return skipOf(operation, "capacity_exceeded", widgetTargetKey(operation.to));
    }
    void spec;
    const [item] = from.splice(index, 1);
    const at = operation.toIndex === undefined
        ? to.length
        : Math.max(0, Math.min(operation.toIndex, to.length));
    to.splice(at, 0, item);
    const fromKey = widgetTargetKey(fromPath);
    const toKey = widgetTargetKey(operation.to);
    const newItemKey = `${toKey}[${leaf.itemId}]`;
    if (newItemKey !== targetKey) {
        // The item's canonical key moved with it; carry its revision subtree along
        // so a later baseline still resolves to the same history.
        for (const key of Object.keys(ctx.targets)) {
            if (!isDescendantTargetKey(key, targetKey))
                continue;
            const moved = `${newItemKey}${key.slice(targetKey.length)}`;
            ctx.targets[moved] = ctx.targets[key];
            delete ctx.targets[key];
        }
    }
    bump(ctx, fromKey);
    bump(ctx, toKey);
    bump(ctx, newItemKey);
    return {
        skipped: null,
        applied: {
            operationId: operation.operationId,
            kind: "move_item",
            targetKey: newItemKey,
        },
        changedTargets: [fromKey, toKey, newItemKey],
    };
}
// --------------------------------------------------------------- helpers
function skipOf(operation, reason, targetKey, extra) {
    return {
        skipped: {
            operationId: operation.operationId,
            reason,
            targetKey,
            ...extra,
        },
    };
}
/**
 * Attribution is OMITTED, never set to `undefined`.
 *
 * `WidgetSkippedOperation.actorId` is optional and the package compiles under
 * `exactOptionalPropertyTypes`, so `{ actorId: undefined }` is not the same
 * thing as an absent key — and the distinction is the product rule, not just a
 * type rule: an absent `actorId` means "we could not attribute this", which is
 * what makes the client fall back to the unattributed copy instead of naming a
 * person. A present-but-undefined key would serialise to nothing over the wire
 * anyway, but it would let a reader write `"actorId" in skip` and get the wrong
 * answer.
 */
function attribution(actorId) {
    return actorId === null || actorId === undefined ? {} : { actorId };
}
function bump(ctx, key) {
    const current = ctx.targets[key]?.revision ?? 0;
    ctx.targets[key] = {
        revision: current + 1,
        actorId: ctx.actorId,
        source: ctx.source,
        // The row version this revision will be committed at. A command that
        // changes anything increments the row by exactly one, and `syncedVersion`
        // is the version the caller read under the lock — so this is that write's
        // version, recorded before it happens. See `WidgetTargetRevision.changedAt`.
        changedAt: ctx.meta.syncedVersion + 1,
    };
}
/**
 * Operations preserve their submitted order, and so do their groups: the first
 * operation of a group fixes where that group runs. Callers therefore control
 * dependency ordering by ordering their operations, without a separate graph.
 */
function groupOperations(operations) {
    const groups = [];
    const byId = new Map();
    for (const operation of operations) {
        if (operation.groupId === undefined) {
            groups.push([operation]);
            continue;
        }
        const existing = byId.get(operation.groupId);
        if (existing) {
            existing.push(operation);
            continue;
        }
        const created = [operation];
        byId.set(operation.groupId, created);
        groups.push(created);
    }
    return groups;
}
/**
 * Validate the operation's path against the merge map — the guard that keeps
 * this from being a generic JSON patch. An unmapped field or collection is
 * refused, so an operation can only reach content a real editing control or a
 * declared semantic action actually edits.
 */
function resolveSpec(map, operation) {
    const path = operation.target;
    if (path.length === 0)
        return null;
    let collections = map.collections ?? [];
    let fields = map.fields;
    let systemFields = map.systemFields ?? [];
    let records = [];
    /**
     * Item-level value sets reachable from HERE, and only from here.
     *
     * Seeded empty and refilled on every step into an item, so an entry-level
     * `valueSets` (`impactEffort.excludedIds`) stays
     * unreachable and a value set declared on one collection cannot be addressed
     * through another.
     */
    let itemValueSets = [];
    let current = null;
    /**
     * The exclusion set is addressable per MEMBER, and only by the two operations
     * that exist for it.
     *
     * Gating on the operation kind here rather than inside the handler is what
     * keeps the widening honest in both directions: `set_field` still cannot
     * reach `excludedIds` (whole-list replacement stays refused, which is what
     * stops a model un-parking everything), and `exclude_item` / `include_item`
     * cannot reach anything else.
     */
    const exclusionOperation = operation.kind === "exclude_item" || operation.kind === "include_item";
    if (exclusionOperation) {
        const exclusion = map.exclusion;
        const leaf = path[path.length - 1];
        if (exclusion === undefined ||
            path.length !== 1 ||
            leaf.kind !== "entry" ||
            leaf.record !== exclusion.valueSet) {
            return null;
        }
        return { collection: null, record: { record: leaf.record, keySource: "" } };
    }
    for (let index = 0; index < path.length; index += 1) {
        const segment = path[index];
        const last = index === path.length - 1;
        if (segment.kind === "prop") {
            if (systemFields.includes(segment.name))
                return null;
            if (fields.includes(segment.name)) {
                return last ? { collection: current, record: null } : null;
            }
            // A declared item value set is a LEAF: the whole list is the value, so it
            // can only be the end of the path. Anything deeper would be addressing a
            // member, which bare strings have no identity for.
            if (itemValueSets.includes(segment.name)) {
                return last ? { collection: current, record: null } : null;
            }
            // A nested object group (for example, processTrace `por`).
            const group = (map.groups ?? []).find((g) => g.group === segment.name);
            if (group && index === 0 && !last) {
                fields = group.fields;
                collections = [];
                records = [];
                systemFields = [];
                itemValueSets = [];
                continue;
            }
            return null;
        }
        if (segment.kind === "entry") {
            const record = records.find((r) => r.record === segment.record);
            if (!record || !last)
                return null;
            return { collection: current, record };
        }
        const name = segment.kind === "collection" ? segment.name : segment.collection;
        const spec = collections.find((c) => c.collection === name);
        if (!spec)
            return null;
        if (segment.kind === "collection") {
            // A bare collection is only addressable as an add target, and only as the
            // final segment.
            return last ? { collection: spec, record: null } : null;
        }
        current = spec;
        collections = spec.children ?? [];
        fields = spec.fields;
        records = spec.records ?? [];
        systemFields = [];
        itemValueSets = spec.itemValueSets ?? [];
        if (last)
            return { collection: spec, record: null };
    }
    return null;
}
function resolveCollectionSpec(map, path) {
    let collections = map.collections ?? [];
    let found = null;
    for (const segment of path) {
        const name = segment.kind === "collection"
            ? segment.name
            : segment.kind === "item"
                ? segment.collection
                : null;
        if (name === null)
            return null;
        const spec = collections.find((c) => c.collection === name);
        if (!spec)
            return null;
        found = spec;
        collections = spec.children ?? [];
    }
    return found;
}
/**
 * Navigate to the object a leaf lives on, or `undefined` if the path is dead.
 *
 * The MAP is required, not decorative. An earlier version matched an item by a
 * hardcoded `entry["id"]` with a fallback scan of `entry["key"]`, which was
 * already a guess and became a wrong one the moment a collection acquired a
 * composite identity: a matrix cell keyed on (severity, level) has neither
 * property, so every operation inside one resolved to nothing and came back as
 * `item_deleted`. `widgetItemIdentity` is now the single definition of what an
 * item's id is, here as everywhere else.
 */
function resolveContainer(root, path, map) {
    let node = root;
    for (let index = 0; index < path.length; index += 1) {
        const segment = path[index];
        if (segment.kind === "prop") {
            node = asRecord(node)?.[segment.name];
        }
        else if (segment.kind === "item") {
            const array = asRecord(node)?.[segment.collection];
            if (!Array.isArray(array))
                return undefined;
            const spec = resolveCollectionSpec(map, path.slice(0, index + 1));
            if (spec === null)
                return undefined;
            node = array.find((entry) => widgetItemIdentity(spec, entry) === segment.itemId);
        }
        else if (segment.kind === "entry") {
            node = asRecord(asRecord(node)?.[segment.record])?.[segment.key];
        }
        else {
            node = asRecord(node)?.[segment.name];
        }
        if (node === undefined || node === null)
            return undefined;
    }
    return node;
}
function resolveArray(root, path, map) {
    const leaf = path[path.length - 1];
    if (leaf?.kind !== "collection")
        return null;
    const parent = resolveContainer(root, path.slice(0, -1), map);
    const array = asRecord(parent)?.[leaf.name];
    return Array.isArray(array) ? array : null;
}
function collectionItemIdsAt(root, map, collectionKey) {
    const path = parseTargetKey(collectionKey);
    if (path === null)
        return null;
    const spec = resolveCollectionSpec(map, path);
    if (spec === null || spec.itemIdField === null)
        return null;
    const array = resolveArray(root, path, map);
    if (array === null)
        return null;
    return array
        .map((entry) => widgetItemIdentity(spec, entry))
        .filter((id) => id !== null);
}
/** Inverse of `widgetTargetKey`, for keys the executor round-trips internally. */
export function parseTargetKey(key) {
    if (key === "")
        return null;
    const segments = [];
    for (const raw of key.split("/")) {
        const item = /^([^[\]{}]+)\[([^[\]]+)\]$/.exec(raw);
        if (item) {
            segments.push({ kind: "item", collection: item[1], itemId: item[2] });
            continue;
        }
        const entry = /^([^[\]{}]+)\{([^{}]+)\}$/.exec(raw);
        if (entry) {
            segments.push({ kind: "entry", record: entry[1], key: entry[2] });
            continue;
        }
        if (/[[\]{}]/.test(raw))
            return null;
        segments.push({ kind: "collection", name: raw });
    }
    return segments;
}
/**
 * Drop revisions for targets that no longer exist, so the metadata stays
 * proportional to live content rather than to a widget's whole edit history.
 * Tombstones are deliberately NOT pruned here — their whole job is to outlive
 * the item.
 */
function pruneTargets(targets, params, map) {
    const live = new Set();
    walkCollections(map.collections ?? [], "", (spec, path) => {
        void spec;
        live.add(path);
    });
    const kept = {};
    for (const [key, value] of Object.entries(targets)) {
        const path = parseTargetKey(key);
        if (path === null)
            continue;
        const hasItem = path.some((segment) => segment.kind === "item");
        if (!hasItem) {
            kept[key] = value;
            continue;
        }
        // Keep an item-scoped revision only while the item is still reachable.
        if (resolveContainer(params, path, map) !== undefined)
            kept[key] = value;
    }
    return kept;
}
function capTombstones(tombstones) {
    const entries = Object.entries(tombstones);
    if (entries.length <= WIDGET_TOMBSTONE_LIMIT)
        return tombstones;
    entries.sort((a, b) => b[1].at - a[1].at);
    return Object.fromEntries(entries.slice(0, WIDGET_TOMBSTONE_LIMIT));
}
/**
 * The shallowest tombstoned item on (or above) this operation's target, or
 * `null`. For an addition the candidate is the item the operation would create;
 * for everything else it is each ancestor item in turn.
 */
function tombstonedAncestor(operation, ctx) {
    if (operation.kind === "add_item") {
        const item = operation.value;
        if (!isRecord(item))
            return null;
        const id = item["id"];
        if (typeof id !== "string")
            return null;
        const key = `${widgetTargetKey(operation.target)}[${id}]`;
        return ctx.tombstones[key] === undefined ? null : key;
    }
    for (let depth = 1; depth <= operation.target.length; depth += 1) {
        const segment = operation.target[depth - 1];
        if (segment.kind !== "item")
            continue;
        const key = widgetTargetKey(operation.target.slice(0, depth));
        if (ctx.tombstones[key] !== undefined)
            return key;
    }
    return null;
}
function sameIdSet(a, b) {
    if (a.length !== b.length)
        return false;
    const left = [...a].sort();
    const right = [...b].sort();
    return left.every((value, index) => value === right[index]);
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function asRecord(value) {
    return isRecord(value) ? value : undefined;
}
function clone(value) {
    return structuredClone(value);
}
//# sourceMappingURL=widget-operations.js.map